import { Plugin, Context } from '@nuxt/types';
import featureFlags from '../lib/featureFlags';

// Hubspot global variables
window._hsq = window._hsq || []; // used for events (e.g. tracking, identifying)
window._hsp = window._hsp || []; // used for privacy (e.g. cookies)

// Convenience variables
const _hsq = window._hsq;
const _hsp = window._hsp;

// Clear Hubspot tracking cookies. `clearCookies` is called on user logout and,
// in conjunction with `identifyUser`, prevents the activity of users who use
// the same browser from being linked to the same contact on Hubspot. A new set of
// cookies is set on the next page load of the app.
//
// `clearCookies` does not depend on the Hubspot feature flag as we always want
// the current Hubspot cookies to be cleared on logout so that there is less
// chance that two users' activity will be merged.
const clearCookies = (): void => {
  _hsp.push(['revokeCookieConsent']);
};

// Link Hubspot tracking cookies to a specific user so their activity will
// be linked to their corresponding Hubspot contact. If no contact on Hubspot
// has this email, a new contact will be created. The email is only linked to
// the cookie (and its associated activity) when `trackPageView` is called.
const identifyUser = (context: Context): void => {
  if (!isHubspotSync(context)) return;

  _hsq.push(['identify', { email: context.store.state.user.email }]);
};

// Track a page view. Tracking a page view on an SPA involves explicitly identifying
// which route has been viewed. Hubspot uses document.title as the page title for a
// page view.
//
// Currently, document.title gets changed in the head() hook of the loaded
// page component and `trackPageView` is called in middleware. Because
// middleware is called before head(), the title sent to Hubspot is one page view
// 'behind'. In order to avoid confusion when viewing activity on Hubspot, the current
// solution is to set the title to a blank string, which shows up on Hubspot as the
// contact having viewed 'a page'.
const trackPageView = (context: Context): void => {
  if (!isHubspotSync(context)) return;

  const originalTitle = document.title;
  document.title = '';

  _hsq.push(['setPath', context.route.fullPath]);
  _hsq.push(['trackPageView']);

  document.title = originalTitle;
};

// Convenience function to identify and track user.
const identifyAndTrack = (context: Context): void => {
  if (!isHubspotSync(context)) return;

  identifyUser(context);
  trackPageView(context);
};

const hubspot: Plugin = (context: Context, inject) => {
  inject('hubspot', {
    clearCookies,
    identifyUser: () => identifyUser(context),
    trackPageView: () => trackPageView(context),
    identifyAndTrack: () => identifyAndTrack(context)
  });
};

const isHubspotSync = (context: Context): boolean => {
  return (
    featureFlags.hubspotSync && context.store.state.user.source !== 'Telkom'
  );
};

export default hubspot;
